import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { emitHealthDataUpdated, onHealthDataUpdated } from "../lib/healthSync";

const VitalsContext = createContext(null);

export const useVitals = () => useContext(VitalsContext);

export const VitalsProvider = ({ children }) => {
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLatest = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/vitals/latest");
      if (res.data && res.data.vitals) {
        const v = res.data.vitals;
        const lastArrayValue = (arr) => Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : undefined;
        const lastNumeric = (arr) => {
          const last = lastArrayValue(arr);
          if (!last) return null;
          if (typeof last === 'number') return last;
          if (typeof last === 'object' && last !== null && typeof last.value === 'number') return last.value;
          return null;
        };
        const unwrapBp = (bp) => {
          if (!bp) return undefined;
          if (typeof bp === 'string') return bp;
          const last = lastArrayValue(bp);
          if (typeof last === 'object' && last !== null) {
            if (last.raw) return last.raw;
            if (last.systolic && last.diastolic) return `${last.systolic}/${last.diastolic}`;
          }
          return undefined;
        };
        setVitals({
          id: v._id,
          bp: unwrapBp(v.bloodPressure) ?? (v.bloodPressure?.systolic && v.bloodPressure?.diastolic ? `${v.bloodPressure.systolic}/${v.bloodPressure.diastolic}` : undefined),
          heart: lastNumeric(v.heartRate),
          temp: lastNumeric(v.temperature),
          oxygen: lastNumeric(v.oxygenLevel),
          weight: lastNumeric(v.weight),
          raw: v,
        });
      }
    } catch (err) {
      console.warn("Failed to fetch latest vitals", err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (opts = {}) => {
    try {
      const q = new URLSearchParams(opts).toString();
      const res = await api.get(`/api/vitals?${q}`);
      return res.data.vitals || [];
    } catch (err) {
      console.warn("Failed to fetch vitals list", err?.message || err);
      return [];
    }
  };

  const submitReading = async (payload) => {
    try {
      const latestRaw = vitals?.raw || {};
      const hasField = (field) => Object.prototype.hasOwnProperty.call(payload || {}, field);

      const mergedPayload = {
        ...payload,
        bloodPressure: hasField("bloodPressure")
          ? payload.bloodPressure
          : (latestRaw?.bloodPressure || undefined),
        heartRate: hasField("heartRate") ? payload.heartRate : latestRaw?.heartRate,
        temperature: hasField("temperature") ? payload.temperature : latestRaw?.temperature,
        oxygenLevel: hasField("oxygenLevel") ? payload.oxygenLevel : latestRaw?.oxygenLevel,
        bloodSugar: hasField("bloodSugar") ? payload.bloodSugar : latestRaw?.bloodSugar,
        weight: hasField("weight") ? payload.weight : latestRaw?.weight,
        hydration: hasField("hydration") ? payload.hydration : latestRaw?.hydration,
      };

      const res = await api.post("/api/vitals", mergedPayload);
      if (res.data && res.data.vitals) {
        // Update latest
        await fetchLatest();
        emitHealthDataUpdated("vitals:submitReading");
        return res.data;
      }
      return res.data;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchLatest();
  }, []);

  useEffect(() => {
    const unsubscribe = onHealthDataUpdated(() => {
      fetchLatest();
    }, { debounceMs: 250 });

    return unsubscribe;
  }, []);

  return (
    <VitalsContext.Provider value={{ vitals, loading, fetchLatest, fetchList, submitReading, setVitals }}>
      {children}
    </VitalsContext.Provider>
  );
};

export default VitalsContext;
